<template>
    <Toast />
    <div class="grid reporting">
        <div class="col-12">
            <div class="card">
                <h5>Outdated Vendor Partner Emails</h5>
                <p>
                    Vendor partner contacts whose email addresses are no longer reachable.
                    Update the contact in the vendor profile to remove them from this list.
                </p>

                <DataTable
                    :value="rows"
                    :loading="loading"
                    :scrollable="true"
                    responsiveLayout="scroll"
                    :paginator="true"
                    :rows="50"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} contacts"
                    :sortField="'last_bounced_at'"
                    :sortOrder="-1"
                >
                    <template #empty>
                        <div class="p-text-center">No outdated vendor partner emails. Nice and clean.</div>
                    </template>

                    <Column field="vendor_partner_name" header="Vendor Partner Name" sortable style="min-width: 14rem">
                        <template #body="slotProps">
                            {{ slotProps.data.vendor_partner_name || 'N/A' }}
                        </template>
                    </Column>

                    <Column field="contact_name" header="Contact's Name" sortable style="min-width: 12rem">
                        <template #body="slotProps">
                            <span>{{ slotProps.data.contact_name || '—' }}</span>
                            <span v-if="slotProps.data.is_primary" class="primary-marker">Primary</span>
                        </template>
                    </Column>

                    <Column field="contact_email" header="Contact's Email" sortable style="min-width: 18rem">
                        <template #body="slotProps">
                            <span class="contact-email">{{ slotProps.data.contact_email }}</span>
                            <EmailDeliverabilityBadge
                                :status="slotProps.data.status"
                                :last-bounced-at="slotProps.data.last_bounced_at"
                                :last-diagnostic="slotProps.data.last_diagnostic"
                            />
                        </template>
                    </Column>

                    <Column field="domain_has_mx" header="Domain MX" sortable style="min-width: 8rem">
                        <template #body="slotProps">
                            <span v-if="slotProps.data.domain_has_mx === true" class="mx-yes">Yes</span>
                            <span v-else-if="slotProps.data.domain_has_mx === false" class="mx-no">No</span>
                            <span v-else>—</span>
                        </template>
                    </Column>

                    <Column field="likely_cause" header="Likely Cause" sortable style="min-width: 18rem">
                        <template #body="slotProps">
                            {{ slotProps.data.likely_cause || '—' }}
                        </template>
                    </Column>

                    <Column field="last_bounced_at" header="Last Bounced" sortable style="min-width: 10rem">
                        <template #body="slotProps">
                            {{ formatDate(slotProps.data.last_bounced_at) }}
                        </template>
                    </Column>

                    <Column header="" style="min-width: 8rem">
                        <template #body="slotProps">
                            <Button
                                label="Update"
                                icon="pi pi-pencil"
                                class="p-button-sm p-button-outlined"
                                @click="openVendorProfile(slotProps.data)"
                            />
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script>
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import EmailDeliverabilityBadge from '../components/EmailDeliverabilityBadge.vue';
import OutdatedEmailsService from '../service/OutdatedEmailsService';
import { setAccountId } from '../service/common';

const service = new OutdatedEmailsService();

export default {
    name: 'OutdatedVendorPartnerEmails',
    components: {
        DataTable,
        Column,
        Button,
        Toast,
        EmailDeliverabilityBadge
    },
    data() {
        return {
            rows: [],
            loading: false
        };
    },
    async mounted() {
        await this.fetchRows();
    },
    methods: {
        async fetchRows() {
            this.loading = true;
            try {
                const response = await service.getOutdatedVendorEmails();
                this.rows = response?.data?.rows || [];
            } catch (err) {
                console.error('Failed to load outdated vendor emails:', err);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Could not load report',
                    detail: 'Try refreshing the page. If the problem persists, contact support.',
                    life: 5000
                });
                this.rows = [];
            } finally {
                this.loading = false;
            }
        },
        formatDate(value) {
            if (!value) return '—';
            try {
                const d = new Date(value);
                if (isNaN(d.getTime())) return '—';
                return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
            } catch (_) {
                return '—';
            }
        },
        openVendorProfile(row) {
            // Switch the active account context to the target vendor, mirroring
            // what the sidebar's onMenuItemClick does (AppSubmenu.vue calls
            // setAccountId() before navigating to /partner/profile). Without
            // this the profile page reads getAccountId() from localStorage and
            // loads whichever vendor was previously selected, not the one ops
            // clicked Update on.
            setAccountId(row.account_id);
            this.$router.push({
                path: '/partner/profile',
                query: {
                    highlight: row.contact_email
                }
            });
        }
    }
};
</script>

<style scoped>
.contact-email {
    font-family: monospace;
}
.primary-marker {
    display: inline-block;
    margin-left: 8px;
    padding: 1px 6px;
    background: #e3f2fd;
    color: #1565c0;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
}
.mx-yes {
    color: #2e7d32;
    font-weight: 600;
}
.mx-no {
    color: #c62828;
    font-weight: 600;
}
</style>